import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingContext } from '../LoadingContext';
import Loading from './Loading';
import './AdminOrderList.css';
import { AuthContext } from '../AuthContext';
import { DateRangePicker, DateRange } from 'react-date-range';
import { addDays, format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ORDER_FILTERS_KEY = 'adminOrderListFilters';

const loadSavedFilters = () => {
  try {
    const raw = sessionStorage.getItem(ORDER_FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      searchTerm: parsed.searchTerm || '',
      statusFilter: parsed.statusFilter || '',
      currentPage: parsed.currentPage || 1,
      range: parsed.range
        ? [{
            startDate: new Date(parsed.range.startDate),
            endDate: new Date(parsed.range.endDate),
            key: 'selection',
          }]
        : null,
    };
  } catch (e) {
    return null;
  }
};

const AdminOrderList = () => {
  const saved = loadSavedFilters();

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(saved?.currentPage || 1);
  const [ordersPerPage] = useState(10);
  const { setIsLoading } = useContext(LoadingContext);
  const { authFetch } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showPicker, setShowPicker] = useState(false);
  const [range, setRange] = useState(saved?.range || [
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);

  const getTodayFormatted = () => {
    const today = new Date();
    const month = new Intl.DateTimeFormat('en-US', { month: '2-digit', timeZone: 'Asia/Kuala_Lumpur' }).format(today);
    const day = new Intl.DateTimeFormat('en-US', { day: '2-digit', timeZone: 'Asia/Kuala_Lumpur' }).format(today);
    const year = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: 'Asia/Kuala_Lumpur' }).format(today);
    return `${year}-${month}-${day}`;
  };

  const [statusFilter, setStatusFilter] = useState(saved?.statusFilter || '');
  const [dateFilter, setDateFilter] = useState(getTodayFormatted);
  const [searchTerm, setSearchTerm] = useState(saved?.searchTerm || '');
  const isInitialFilterRun = useRef(true);

  useEffect(() => {
    try {
      sessionStorage.setItem(ORDER_FILTERS_KEY, JSON.stringify({
        searchTerm,
        statusFilter,
        currentPage,
        range: {
          startDate: range[0].startDate.toISOString(),
          endDate: range[0].endDate.toISOString(),
        },
      }));
    } catch (e) {
      // ignore storage errors
    }
  }, [searchTerm, statusFilter, currentPage, range]);

  useEffect(() => {
    fetchOrders();
  }, [setIsLoading]);

  const fetchOrders = async () => {
    setIsLoading(true);
    setLoading(true);
    try {

      const response = await authFetch(`${API_BASE_URL}/api/orders?status=` + statusFilter + "&start=" + format(range[0].startDate, 'yyyy-MMM-dd') + "&end=" + format(range[0].endDate, 'yyyy-MMM-dd'));
      if (!response.ok) {
        // Check if the response is 404 (Not Found) which could mean no orders exist
        if (response.status === 404) {
          // Handle case when no orders exist in the database
          setOrders([]);
          setFilteredOrders([]);
          setError(null);
        } else {
          throw new Error('Failed to fetch orders');
        }
      } else {
        const data = await response.json();
        // Handle empty array case explicitly
        if (Array.isArray(data) && data.length === 0) {
          setOrders([]);
          setFilteredOrders([]);
          setError(null);
        } else {
          const sortedData = [...data].sort((a, b) =>
            new Date(b.orderDate) - new Date(a.orderDate)
          );
          setOrders(sortedData);
          setFilteredOrders(sortedData);
          setError(null);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  // Filter orders based on tab selection and filters
  useEffect(() => {
    let result = [...orders];

    // if (activeTab !== 'all') {
    //   result = result.filter(order => order.orderStatus.toLowerCase() === activeTab);
    // }

    // if (statusFilter) {
    //   result = result.filter(order => order.orderStatus.toLowerCase() === statusFilter.toLowerCase());
    // }

    // if (dateFilter) {
    //   const filterDate = new Date(dateFilter);
    //   filterDate.setHours(0, 0, 0, 0);

    //   result = result.filter(order => {
    //     const orderDate = new Date(order.orderDate);
    //     orderDate.setHours(0, 0, 0, 0);
    //     return orderDate.getTime() === filterDate.getTime();
    //   });
    // }

    // Apply search term filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(order =>
        (order.orderNum && order.orderNum.toString().includes(search)) ||
        (order.email && order.email.toLowerCase().includes(search)) ||
        (order.username && order.username.toLowerCase().includes(search)) ||
        (order.phoneNumber && order.phoneNumber.includes(search))
      );
    }

    setFilteredOrders(result);
    if (isInitialFilterRun.current) {
      isInitialFilterRun.current = false;
    } else {
      setCurrentPage(1);
    }
  }, [searchTerm, orders]);

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const Filters = () => {
    // setStatusFilter('');
    // setDateFilter('');
    // setSearchTerm('');
    // setActiveTab('all');
    fetchOrders();
  };

  const getStatusClass = (status) => {
    const statusLower = status.toLowerCase().replace(/ /g, '-');
    switch (statusLower) {
      case "paid":
        return "admin-order-list-status-completed";
      case "failed":
        return "admin-order-list-status-cancelled";
      case "order-processing":
        return "admin-order-list-status-order-processing";
      case "pending-payment":
        return "admin-order-list-status-pending-payment";
      case "awaiting-delivery":
        return "admin-order-list-status-awaiting-delivery";
      case "awaiting-pickup":
        return "admin-order-list-status-awaiting-pickup";
      case "partially-shipped":
        return "admin-order-list-status-partially-shipped";
      case "out-for-delivery":
        return "admin-order-list-status-out-for-delivery";
      case "completed":
        return "admin-order-list-status-completed";
      case "cancelled":
        return "admin-order-list-status-cancelled";
      case "delayed":
        return "admin-order-list-status-delayed";
      case "returned":
        return "admin-order-list-status-returned";
      case "refunded":
        return "admin-order-list-status-refunded";
      default:
        return '';
    }
  };

  const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; 
        } catch (e) {
            return dateString;
        }
    };

  if (loading) {
    return (
      <div className="admin-order-list-container">
        <div className="admin-order-list-loading">
          <Loading />
        </div>
      </div>
    );
  }

  if (error && error !== 'Failed to fetch orders') {
    return (
      <div className="admin-order-list-container">
        <div className="admin-order-list-error">Error: {error}</div>
        <button
          className="admin-order-list-back-btn"
          onClick={() => navigate('/admin/dashboard')}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="admin-order-list-container">
      {/* Back to Dashboard Button */}
      <div className="admin-order-list-header">
        <button
          className="admin-order-list-back-btn"
          onClick={() => navigate('/admin/dashboard')}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      {/* Title Section */}
      <div className="admin-order-list-title">
        <h1>Order Management</h1>
      </div>

      {/* Filter Section */}
      <div className="admin-order-list-filter">
        <div className="admin-order-list-filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search by order number, email, username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="admin-order-list-filter-group">
          <label>Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="order processing">Order Processing</option>
            <option value="pending payment">Pending Payment</option>
            <option value="awaiting delivery">Awaiting Delivery</option>
            <option value="awaiting pickup">Awaiting Pickup</option>
            <option value="partially shipped">Partially Shipped</option>
            <option value="out for delivery">Out for Delivery</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="delayed">Delayed</option>
            <option value="returned">Returned</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        <div className="admin-order-list-filter-group">
          <label>Order Date</label>
          <div style={{ position: 'relative' }}>
            <input style={{ width : "100%" }}
              value={`${format(range[0].startDate, 'dd-MMM-yyyy')}    to   ${format(range[0].endDate, 'dd-MMM-yyyy')}`}
              onClick={() => setShowPicker(!showPicker)}
              readOnly
            />
            {showPicker && (
              <div style={{
                position: 'absolute',
                zIndex: 10,
                top: '100%', // or any offset
                right: 0,
                background: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}>
                <DateRangePicker
                  locale={enUS}
                  editableDateInputs={true}
                  onChange={item => setRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={range}
                />
              </div>
            )}

          </div>

          {/* <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          /> */}

        </div>
        <button
          className="admin-order-list-filter-btn"
          onClick={Filters}
        >
         Search
        </button>
      </div>

      {/* Orders Table Section */}
      <div className="admin-order-list-table-container">
        {currentOrders.length > 0 ? (
          <table className="admin-order-list-table">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Order Date</th>
                <th>Email</th>
                <th>Username</th>
                <th>Phone Number</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order) => (
                <tr key={order.orderId}>
                  <td>#{order.orderNum}</td>
                  <td>{formatDate(order.orderDate)}</td>
                  <td>{order.email}</td>
                  <td>{order.username || 'Guest'}</td>
                  <td>{order.phoneNumber}</td>
                  <td>{order.paymentMethod}</td>
                  <td>
                    <span className={`admin-order-list-status ${getStatusClass(order.orderStatus)}`}>
                      {order.orderStatus}
                    </span>
                  </td>
                  <td className="admin-order-list-amount">RM{(order.finalAmount).toFixed(2)}</td>
                  <td>
                    <button
                      className="admin-order-list-view-btn"
                      onClick={() => navigate(`/admin/orders/${order.orderId}`)}
                    >
                      <i className="fas fa-eye"></i> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="admin-order-list-empty">
            <i className="fas fa-shopping-cart"></i>
            <p className="primary-message">No order found matching your filters.</p>
            <p className="secondary-message">The default status will reflect the new status and current day's data.
              To view all orders, please click the reset button.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredOrders.length > ordersPerPage && (
        <div className="admin-order-list-pagination">
          <button
            className="admin-order-list-pagination-btn"
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button
            className="admin-order-list-pagination-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {[...Array(totalPages).keys()].map((number) => {

            if (
              number + 1 === 1 ||
              number + 1 === totalPages ||
              (number + 1 >= currentPage - 1 && number + 1 <= currentPage + 1)
            ) {
              return (
                <button
                  key={number + 1}
                  className={`admin-order-list-pagination-btn ${currentPage === number + 1 ? 'active' : ''
                    }`}
                  onClick={() => paginate(number + 1)}
                >
                  {number + 1}
                </button>
              );
            }

            if (
              (number + 1 === currentPage - 2 && currentPage > 3) ||
              (number + 1 === currentPage + 2 && currentPage < totalPages - 2)
            ) {
              return (
                <span key={number + 1} className="admin-order-list-pagination-btn">
                  ...
                </span>
              );
            }
            return null;
          })}

          <button
            className="admin-order-list-pagination-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-right"></i>
          </button>
          <button
            className="admin-order-list-pagination-btn"
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminOrderList;